import type { FC } from 'react';
import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FormDesktop as Form } from '@alfa-bank/site-form/desktop';
import { TextDesktop as Text } from '@alfa-bank/site-text-v2/desktop';
import { PlateDesktop as Plate } from '@alfa-bank/site-plate/desktop';
import { InformationCircleMIcon } from '@alfalab/icons-glyph/InformationCircleMIcon';
import type { TFormValues } from '@alfa-bank/site-form/src/models';
import type { FormApi } from 'final-form';
import { InvestAddressRegistrationField } from '@alfa-bank/site-invest-address-registration-field';
import { BlockDesktop as Block } from '@alfa-bank/site-block/desktop';
import { ButtonDesktop as Button } from '@alfa-bank/site-button-v2/desktop';
import { FormProgressBarV2Desktop } from '@alfa-bank/site-form-progress-bar-v2/desktop';

import { DELIVERY_ADDRESS, DELIVERY_ADDRESS_DADATA, DELIVERY_OFFICE } from '../../config';
import { AnalyticsSpy } from '../../components/analytics-spy';
import type {
    TDispatch,
    // IDeliveryBookOfficeDeliveryFormValues,
    THandleSelectAddress,
    IDeliveryBookOfficeDeliveryFormValues,
} from '../../store';
import {
    selectShowProgressBar,
    selectDeliveryAddressMetaState,
    selectDeliveryBookOfficeDelivery,
    setFormFieldsDeliveryAddressMeta,
    setFormFieldsDeliveryBookOfficeDelivery,
    submitDeliveryAddressMeta,
    submitDeliveryBookOfficeDelivery,
} from '../../store';
import type { TFormSubmitCallback } from '../../models/common-types';
import { DEFAULT_DELIVERY_ADDRESS_SETTINGS } from '../delivery-book-courier-delivery/config/default-values';
import { AddressValidationModalsDesktop } from '../../components/address-validation-modals/desktop';
import { AddressSpinner } from '../../components/address-spinner';
import { sendEventSmev } from '../../helpers/proxy-send-event';

import {
    DEFAULT_ATTENTION_TEXT,
    defaultDeliveryBookDeliverySettingsDesktop,
} from './config/default-values';
import type { TDeliveryBookOfficeDeliveryStepDesktop } from './models';
import { OfficeSelectionDesktop } from './components/office-selection/desktop';
import { analyticsBookOfficeDeliverySmev } from './analytics';

export const DeliveryBookOfficeDeliveryDesktop: FC<TDeliveryBookOfficeDeliveryStepDesktop> = ({
    deliveryAddressLabel = defaultDeliveryBookDeliverySettingsDesktop.deliveryAddressLabel,
    primaryButtonText = defaultDeliveryBookDeliverySettingsDesktop.primaryButtonText,
    subtitle = defaultDeliveryBookDeliverySettingsDesktop.subtitle,
    title = defaultDeliveryBookDeliverySettingsDesktop.title,
    progressBarSettings = defaultDeliveryBookDeliverySettingsDesktop.progressBarSettings,
    deliveryFlow,
}) => {
    const showProgressBar = useSelector(selectShowProgressBar);
    const deliveryAddressMetaState = useSelector(selectDeliveryAddressMetaState);
    const deliveryBookOfficeDeliveryState = useSelector(selectDeliveryBookOfficeDelivery);
    const isLoading =
        deliveryAddressMetaState.isLoading || deliveryBookOfficeDeliveryState.isLoading;
    const dispatch = useDispatch<TDispatch>();
    const analytics = analyticsBookOfficeDeliverySmev[deliveryFlow];

    const handleOnSubmit = useCallback(
        async (
            _: TFormValues,
            formApi: FormApi<TFormValues>,
            finishSubmitCallback: TFormSubmitCallback
        ) => {
            const fields = formApi.getState()
                .values as unknown as IDeliveryBookOfficeDeliveryFormValues;

            dispatch(setFormFieldsDeliveryBookOfficeDelivery(fields));
            const errors = await dispatch(
                submitDeliveryBookOfficeDelivery({
                    pending: analytics.formBookOfficeDeliveryRequestPending,
                    success: analytics.formBookOfficeDeliveryRequestSuccess,
                    fail: analytics.formBookOfficeDeliveryRequestFail,
                })
            );

            finishSubmitCallback(errors);
        },
        [
            analytics.formBookOfficeDeliveryRequestFail,
            analytics.formBookOfficeDeliveryRequestPending,
            analytics.formBookOfficeDeliveryRequestSuccess,
            dispatch,
        ]
    );

    // TODO нужно для всех шагов вынести это в хук useDeliveryAddressMeta
    const handleOnSelect: THandleSelectAddress = (addressValue, data, isValid) => {
        if (isValid) {
            sendEventSmev(analytics.selectValidAddressClick);
            dispatch(setFormFieldsDeliveryAddressMeta({ dadata: data, addressValue }));
            dispatch(
                submitDeliveryAddressMeta({
                    pending: analytics.formChooseDeliveryAddressRequestPending,
                    success: analytics.formChooseDeliveryAddressRequestSuccess,
                    fail: analytics.formChooseDeliveryAddressRequestFail,
                    failAddressOutOfRange:
                        analytics.formChooseDeliveryAddressRequestFailAddressOutOfRange,
                    failAddressNotFound:
                        analytics.formChooseDeliveryAddressRequestFailAddressNotFound,
                })
            );
        } else {
            sendEventSmev(analytics.selectNotValidAddressClick);
        }
    };

    return (
        <Form
            analytics={{
                submit: analytics.formSubmitSuccess,
                submitOnFail: analytics.formSubmitFail,
            }}
            initialValues={{
                [DELIVERY_ADDRESS]: deliveryAddressMetaState.formState[DELIVERY_ADDRESS],
                [DELIVERY_ADDRESS_DADATA]:
                    deliveryAddressMetaState.formState[DELIVERY_ADDRESS_DADATA],
                [DELIVERY_OFFICE]: deliveryBookOfficeDeliveryState.formState[DELIVERY_OFFICE],
            }}
            onSubmit={handleOnSubmit}
            view="without-shadow"
        >
            <AnalyticsSpy analytics={analytics} />
            <Text styler={{ marginTop: 0, marginBottom: 24 }} tag="h2" view="medium">
                {title}
            </Text>
            {showProgressBar && (
                <FormProgressBarV2Desktop
                    caption={progressBarSettings.caption}
                    currentStepName={progressBarSettings.currentStepName}
                    hatchValue={progressBarSettings.hatchValue}
                    header={progressBarSettings.header}
                    line={progressBarSettings.line}
                    maxStep={progressBarSettings.maxStep}
                    nextStepName={progressBarSettings.nextStepName}
                    steps={progressBarSettings.steps}
                    styler={{ marginBottom: 24 }}
                    value={progressBarSettings.value}
                />
            )}
            <Text styler={{ marginTop: 0, marginBottom: 24 }} tag="p" view="primary-medium">
                {subtitle}
            </Text>
            <Plate
                // TODO временные стили пока не обновится Plate компонент коровский до версии 7.0.7 где цвета исправлены
                // className={cn(styles.attentionAlert, styles.external)}
                border={false}
                hasCloser={false}
                // TODO цвет fill нужно вынести в стили
                leftAddons={<InformationCircleMIcon fill="#FA9313" height={32} width={32} />}
                styler={{ marginBottom: 24 }}
                view="attention"
            >
                <Text styler={{ marginTop: 0, marginBottom: 0 }} view="primary-small">
                    {DEFAULT_ATTENTION_TEXT}
                </Text>
            </Plate>
            <Block notPadded styler={{ marginBottom: 24 }}>
                <Text styler={{ marginTop: 0, marginBottom: 24 }} tag="p" view="small">
                    {deliveryAddressLabel}
                </Text>
                <InvestAddressRegistrationField
                    disabled={isLoading}
                    isRequired
                    name={DELIVERY_ADDRESS}
                    onSelect={handleOnSelect}
                    settings={DEFAULT_DELIVERY_ADDRESS_SETTINGS}
                    validateType="deliveryAddress"
                />
                <AddressSpinner isLoading={deliveryAddressMetaState.isLoading} Text={Text} />
                <AddressValidationModalsDesktop />
            </Block>
            <Block notPadded styler={{ marginBottom: 24 }}>
                <OfficeSelectionDesktop
                    analytics={{
                        onSelect: analytics.officeItemSelectedClick,
                        onToggleDropdown: analytics.officeDropdownToggleClick,
                    }}
                    offices={deliveryBookOfficeDeliveryState.offices}
                />
            </Block>
            <Button loading={isLoading} size="s" type="submit" view="primary">
                {primaryButtonText}
            </Button>
        </Form>
    );
};
